import { apiClient } from "../App";
import { toast } from "sonner";
import { CheckCircle, AlertCircle, XCircle } from "lucide-react";

/**
 * QuickRsvp — 3 inline RSVP buttons (Going / Maybe / Can't go) used on
 * both the Events list page and the Dashboard "Upcoming Events" card.
 *
 * Props:
 *  - event: { id, status, my_status }
 *  - compact: when true, icon-only buttons (smaller)
 *  - hidden: explicitly hide (e.g. when admin mode is on, parent shows Edit/Delete instead)
 *  - onChange(eventId, newStatus): called after a successful update so the parent
 *    can update its local state. The component itself does NOT manage state.
 *
 * Added iter-66f, promoted to shared component in iter-66g.
 */
const QuickRsvp = ({ event, compact = false, hidden = false, onChange, className = "" }) => {
  if (hidden) return null;
  if (!event) return null;
  if (event.status === "cancelled") return null;

  const my = event.my_status; // null | attending | not_attending | maybe | pending

  const update = async (e, status) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await apiClient.put(`/my-attendance/${event.id}?status=${status}`);
      onChange?.(event.id, status);
      const verb =
        status === "attending" ? "Going" :
        status === "not_attending" ? "Not going" : "Maybe";
      toast.success(`Marked as ${verb}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update RSVP");
    }
  };

  const base =
    "inline-flex items-center justify-center gap-1 rounded-md border transition-all text-xs font-medium select-none";
  const sz = compact ? "h-7 px-2" : "h-8 px-2.5";

  const cls = (active, color) => {
    if (color === "green")
      return active
        ? "bg-green-500/15 border-green-500/40 text-green-400 hover:bg-green-500/25"
        : "border-white/10 text-muted-foreground hover:text-green-400 hover:border-green-500/30";
    if (color === "amber")
      return active
        ? "bg-amber-500/15 border-amber-500/40 text-amber-400 hover:bg-amber-500/25"
        : "border-white/10 text-muted-foreground hover:text-amber-400 hover:border-amber-500/30";
    return active
      ? "bg-red-500/15 border-red-500/40 text-red-400 hover:bg-red-500/25"
      : "border-white/10 text-muted-foreground hover:text-red-400 hover:border-red-500/30";
  };

  return (
    <div
      className={`flex items-center gap-1.5 ${className}`}
      onClick={(e) => e.stopPropagation()}
      data-testid={`quick-rsvp-${event.id}`}
    >
      <button
        type="button"
        onClick={(e) => update(e, "attending")}
        className={`${base} ${sz} ${cls(my === "attending", "green")}`}
        data-testid={`quick-rsvp-attending-${event.id}`}
        aria-label="Going"
        title="Going"
      >
        <CheckCircle className="w-3.5 h-3.5" />
        {!compact && <span>Going</span>}
      </button>
      <button
        type="button"
        onClick={(e) => update(e, "maybe")}
        className={`${base} ${sz} ${cls(my === "maybe", "amber")}`}
        data-testid={`quick-rsvp-maybe-${event.id}`}
        aria-label="Maybe"
        title="Maybe"
      >
        <AlertCircle className="w-3.5 h-3.5" />
        {!compact && <span>Maybe</span>}
      </button>
      <button
        type="button"
        onClick={(e) => update(e, "not_attending")}
        className={`${base} ${sz} ${cls(my === "not_attending", "red")}`}
        data-testid={`quick-rsvp-not_attending-${event.id}`}
        aria-label="Can't make it"
        title="Can't make it"
      >
        <XCircle className="w-3.5 h-3.5" />
        {!compact && <span>Can&apos;t go</span>}
      </button>
    </div>
  );
};

export default QuickRsvp;
